import {useCollapsibleSidebarContext} from '@github-ui/layout-primitives/CollapsibleSidebar'
import {CollapsibleNavList} from '@github-ui/layout-primitives/CollapsibleNavList'
import {useSidebarPageLayoutActions} from '@github-ui/layout-primitives/SidebarPageLayout'
import {SidebarTooltip} from '@github-ui/layout-primitives/SidebarTooltip'
import {SoftNavOnlyLink as Link} from '@github-ui/react-router'
import {ArrowUpRightIcon, type Icon} from '@primer/octicons-react'
import type {ComponentProps} from 'react'
import {useActionsAnalytics} from '../hooks/use-actions-analytics'

export type SidebarAnalyticsKey =
  | 'all-workflows'
  | 'caches'
  | 'deployments'
  | 'attestations'
  | 'runners'
  | 'lockfile'
  | 'usage'
  | 'performance'

interface SidebarNavItemProps {
  analyticsKey: SidebarAnalyticsKey
  id?: string
  label: string
  href: string
  icon: Icon
  isCurrent?: boolean
  softNavigationEnabled?: boolean
  'data-turbo'?: 'false'
  external?: boolean
}

export function SidebarNavItem({
  analyticsKey,
  id,
  label,
  href,
  icon: IconComponent,
  isCurrent = false,
  softNavigationEnabled = false,
  'data-turbo': dataTurbo,
  external = false,
}: SidebarNavItemProps) {
  const track = useActionsAnalytics()
  const {visualIsExpanded} = useCollapsibleSidebarContext()
  const {closeDialog} = useSidebarPageLayoutActions()
  const linkProps = softNavigationEnabled
    ? ({as: Link, to: href} as unknown as ComponentProps<typeof CollapsibleNavList.Item>)
    : {href}
  const item = (
    <CollapsibleNavList.Item
      id={id}
      {...linkProps}
      data-turbo={dataTurbo}
      aria-current={isCurrent ? 'page' : undefined}
      onClick={() => {
        track('workflow_sidebar.navigate', `ref_loc:actions_workflow_sidebar;destination:${analyticsKey}`)
        if (softNavigationEnabled) closeDialog()
      }}
    >
      <CollapsibleNavList.LeadingVisual>
        <IconComponent />
      </CollapsibleNavList.LeadingVisual>
      {label}
      {external && visualIsExpanded ? (
        <CollapsibleNavList.TrailingVisual>
          <ArrowUpRightIcon />
        </CollapsibleNavList.TrailingVisual>
      ) : null}
    </CollapsibleNavList.Item>
  )

  return visualIsExpanded ? item : <SidebarTooltip text={label}>{item}</SidebarTooltip>
}

SidebarNavItem.displayName = 'SidebarNavItem'
